import { reactive, ref, watch } from 'vue'
import { defaultVK } from '@hid/define/define_keys'
import { MacroDetails, PostMagnetSenior } from '@libs/types/response'
import { DriverService } from './driver'
import { DriverSocketMsgType, DriverSocketMsg } from '@libs/types/message'
import { enableDialog, DialogType } from '@/components/cdialog/dialog'
import { showSwitchKeySyncDialog } from '@/components/cdialog/switch_key_sync_dialog'
import i18n from '@/languages/i18n'
import { operate } from './operate'
import { KK, VK } from '@/define/define'
import { macro } from './action_set'
import { LightModeSettingMap, ResSetData, SetMagnetSenior } from '../response'
import { MagnetSeniorType } from '@libs/types/enum'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { resolveReadSettingsRefreshPolicy } from './read_settings_refresh_policy'
import firmware from '@/upgrade/firmware'
import { convertSeniorTravelToSend_Above6800, convertSeniorTravelFromReceive_Above6800 } from './travel_converter'

export enum MapKeyType {
  base = 1,
  Media = 2,
  Mouse = 3,
  Macro = 4,
  Modifier = 5,
  Magnet = 6,
  Fire = 7,
}

const createEmptyLayout = (): ResSetData['Layout'] => {
  return {
    KeyMap: {},
    KeyMapFire: {},
    MacroType: {},
    Macros: {},
    Magenet: {},
    KeyMapModifier: {},
  }
}

const createEmptySetData = (): ResSetData => {
  return {
    LightMode: {} as ResSetData['LightMode'],
    LightModeSettings: {} as LightModeSettingMap,
    LightCustom: {},
    LightLastIsCustom: false,
    Mouse: {} as ResSetData['Mouse'],
    ColorsDynamic: { Run: false } as ResSetData['ColorsDynamic'],
    Decorates: [],
    Layout: createEmptyLayout(),
    LayoutFn: createEmptyLayout(),
  }
}

class Data {
  private data = reactive(createEmptySetData())

  private deleteLock = false

  private onDynamicStateChange: ((isRunning: boolean) => void) | null = null

  private settingsReadPromise: Promise<void> | null = null

  private dataLoaded = false

  private deferredRefreshTimer: ReturnType<typeof setTimeout> | null = null

  readonly isReadingSettings = ref(false)

  private stopDynamicColorWatch: (() => void) | null = null

  init() {
    this.dataLoaded = false
    void this.requestSettingsRefresh()
  }

  async askData() {
    if (this.settingsReadPromise) {
      return this.settingsReadPromise
    }

    this.isReadingSettings.value = true
    this.settingsReadPromise = this.readSettings().finally(() => {
      this.settingsReadPromise = null
      this.isReadingSettings.value = false
    })

    return this.settingsReadPromise
  }

  /** 仅在数据尚未就绪时才发起读取，适用于面板 onMounted 中的懒初始化场景 */
  async ensureDataLoaded() {
    if (this.settingsReadPromise) return this.settingsReadPromise
    if (this.dataLoaded) return
    return this.askData()
  }

  async requestSettingsRefresh(isSaveOp: boolean = false, isLightOp: boolean = false) {
    const policy = resolveReadSettingsRefreshPolicy(isSaveOp, isLightOp)

    if (policy.mode === 'deferred') {
      this.scheduleDeferredSettingsRefresh(policy.delayMs)
      return
    }

    await this.askData()
  }

  isDKS = (kk: KK): boolean => {
    return this.getData().Layout.Magenet[kk] && this.getData().Layout.Magenet[kk].KeyType == MagnetSeniorType.SeniorKeyTypeDKS
  }

  getData() {
    return this.data
  }

  get getActionData() {
    let IsFn = operate.fnCheck.value
    if (IsFn) {
      return this.data.LayoutFn
    } else {
      return this.data.Layout
    }
  }

  setKeyMap(key: KK, value: VK, KeyModifier: VK[], type: MapKeyType = MapKeyType.base) {
    const isFn = operate.fnCheck.value
    const modifier = KeyModifier.length > 0 ? { [key]: KeyModifier } : {}
    return this.saveKeyMap({ [key]: value }, type, modifier, {}, isFn)
  }

  setKeyFire(key: KK, value: number[]) {
    const isFn = operate.fnCheck.value
    return this.saveKeyMap({ [key]: 0 }, MapKeyType.Fire, {}, { [key]: value }, isFn)
  }

  setKeyMedia(key: KK, value: VK) {
    if (this.deleteLock) {
      return
    }
    const isFn = operate.fnCheck.value
    return this.saveKeyMap({ [key]: value }, MapKeyType.Media, {}, {}, isFn)
  }

  watchDynamicColor() {
    if (this.stopDynamicColorWatch) {
      this.stopDynamicColorWatch()
      this.stopDynamicColorWatch = null
    }
    this.stopDynamicColorWatch = watch(this.data.ColorsDynamic, async (_value) => {
      await this.saveDynamicColor()
    })
  }
  private setColorTimeout: NodeJS.Timeout | null = null

  private lightUnlock = () => {
    if (this.setColorTimeout) {
      this.clearCustomColorTimeout()
    }
  }

  setKeysColor = async (newValue: { [index: KK]: number }, oldValue: { [index: KK]: number }) => {
    this.data.LightCustom = { ...newValue }
    // 用于取消自定义颜色，如果没有数据，不需要延迟，直接发送数据
    if (Object.keys(this.data.LightCustom).length === 0) {
      this.saveCustomColor()
      return
    }
    this.setCustomColorInterval(oldValue)
  }
  // 获取延迟时间
  private getDelayTime = async () => {
    if (specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen)) {
      // 1.06一下的固件，出现写入慢的问题，因此延时要增加
      return (await firmware.isHuoChaiRenV106()) ? 400 : 1200
    }
    return 100
  }

  setCustomColorInterval = async (oldValue: { [index: KK]: number }) => {
    if (this.setColorTimeout) {
      clearTimeout(this.setColorTimeout)
    }
    const delayTimer = await this.getDelayTime()
    this.setColorTimeout = setTimeout(async () => {
      try {
        await this.saveCustomColor()
      } catch (error) {
        this.data.LightCustom = oldValue
      } finally {
        this.lightUnlock()
      }
      this.clearCustomColorTimeout()
    }, delayTimer)
  }

  private clearCustomColorTimeout = () => {
    if (this.setColorTimeout) {
      clearTimeout(this.setColorTimeout)
      this.setColorTimeout = null
    }
  }

  // 自定义颜色的返回数据
  getColorKeys = (newValue: { [index: KK]: number }) => {
    const keys = []
    for (const key in newValue) {
      keys.push({ Key: Number(key), Value: newValue[Number(key)] })
    }
    return { Keys: keys, Light: this.data.LightMode.Light }
  }

  // setCustomLight(key: KK, colorValue: number) {
  //   this.data.LightLastIsCustom = true
  //   this.data.LightCustom[key] = colorValue
  // }

  private async saveCustomColor() {
    const data = { Type: DriverSocketMsgType.MTSetKeysColor, Data: this.getColorKeys(this.data.LightCustom) } as DriverSocketMsg<any>
    await DriverService.instance.askTypeAndData(data)
  }

  async saveLightMode() {
    this.lightUnlock()
    const data = {
      Type: DriverSocketMsgType.MTSetLed,
      Data: this.data.LightMode,
    } as DriverSocketMsg<any>
    await DriverService.instance.askTypeAndData(data)
    await this.requestSettingsRefresh(false, true)
  }
  async saveLightDecoration(isShowDialog: boolean = false) {
    this.lightUnlock()
    const { t } = i18n.global
    const data = { Type: DriverSocketMsgType.MTSetLightDecoration, Data: this.data.Decorates } as DriverSocketMsg<any>
    await DriverService.instance.askTypeAndData(data).catch(() => {
      console.log('set decoration failed')
      isShowDialog ? enableDialog(t('setting_failed')) : {}
      return
    })
    await this.requestSettingsRefresh(false, true)
  }

  subscribeOnDynamicStateChange(callback: (isRunning: boolean) => void) {
    this.onDynamicStateChange = callback
  }
  private async saveDynamicColor() {
    this.lightUnlock()
    const data = { Type: DriverSocketMsgType.MTMusicalRhythm, Data: this.data.ColorsDynamic } as DriverSocketMsg<any>
    await DriverService.instance.askTypeAndData<boolean>(data).catch(() => {
      return false
    })
    if (this.onDynamicStateChange) {
      this.onDynamicStateChange(this.data.ColorsDynamic.Run)
    }
  }
  stopDynamicColor() {
    if (this.data.ColorsDynamic.Run) {
      this.data.ColorsDynamic.Run = false
      this.saveDynamicColor()
    }
  }

  private saveKeyMap(values: { [index: KK]: number }, type: MapKeyType, KeyModifier: { [index: KK]: VK[] }, KeyFire: { [index: KK]: number[] }, isFn: boolean) {
    const data = {
      Type: DriverSocketMsgType.MTSetKey,
      Data: {
        Key: values,
        KeyModifier: KeyModifier,
        KeyFire: KeyFire,
        KeyType: type,
        IsFn: isFn,
      },
    } as DriverSocketMsg<any>

    return DriverService.instance.askTypeAndData<{ hasSwitchKey?: boolean }>(data).then(async (res) => {
      await this.requestSettingsRefresh(true)
      const { t } = i18n.global

      if (res?.hasSwitchKey) {
        // 弹出可视化确认弹窗
        const confirmed = await showSwitchKeySyncDialog()
        if (confirmed) {
          enableDialog(t('switch_key_syncing'), undefined, true, 10000)
          try {
            await DriverService.instance.askTypeAndData({ Type: DriverSocketMsgType.MTReplicateSwitchKeys, Data: {} } as DriverSocketMsg<any>)
            enableDialog(t('switch_key_synced_to_all_configs'), undefined, true, 3000)
          } catch {
            enableDialog(t('switch_key_sync_failed'), undefined, false, 3000)
          }
        } else {
          enableDialog(t('switch_key_sync_skipped_warning'), undefined, true, 5000)
        }
      } else {
        enableDialog(t('set_success'))
      }
    })
  }

  saveKeyMacro(key: number, value: macro) {
    const isFn = operate.fnCheck.value
    let macros: MacroDetails = {
      Key: key,
      CycleTimes: value.CycleTimes,
      CycleType: value.CycleType,
      Records: value.Records,
      IsFn: isFn,
    }
    const data = {
      Type: DriverSocketMsgType.MTSetMacro,
      Data: macros,
    } as DriverSocketMsg<any>
    return DriverService.instance.askTypeAndData(data).then(async (_) => {
      await this.requestSettingsRefresh(true)
      const { t } = i18n.global
      enableDialog(t('macro_set_success'))
    })
  }

  saveMagnetSenior(key: number, value: SetMagnetSenior) {
    value = convertSeniorTravelToSend_Above6800(value)
    const isFn = operate.fnCheck.value
    let data = {
      Type: DriverSocketMsgType.MTMagnetSeniorKey,
      Data: {
        Key: key,
        Value: value,
        IsFn: isFn,
      },
    } as DriverSocketMsg<PostMagnetSenior>
    return DriverService.instance.askTypeAndData(data).then(async () => {
      await this.requestSettingsRefresh(true)
      const { t } = i18n.global
      enableDialog(t('setting_success'))
    })
  }

  private async readSettings() {
    try {
      const res: ResSetData = await DriverService.instance.askType<ResSetData>(DriverSocketMsgType.MTReadSettings)
      console.log('read settings data', res)

      if (res) {
        this.data.ColorsDynamic = { ...res.ColorsDynamic }
        this.data.LightLastIsCustom = res.LightLastIsCustom
        this.data.LightCustom = { ...res.LightCustom }
        this.data.LightMode = res.LightMode
        this.data.LightModeSettings = { ...(res.LightModeSettings || {}) }
        this.data.Mouse = { ...res.Mouse }
        this.data.Layout = { ...res.Layout }
        this.data.LayoutFn = { ...res.LayoutFn }
        if (this.data.Layout.Magenet) {
          this.data.Layout.Magenet = convertSeniorTravelFromReceive_Above6800(this.data.Layout.Magenet)
        }
        if (this.data.LayoutFn.Magenet) {
          this.data.LayoutFn.Magenet = convertSeniorTravelFromReceive_Above6800(this.data.LayoutFn.Magenet)
        }
        this.data.Decorates = res.Decorates ? res.Decorates : []

        this.dataLoaded = true
        this.watchDynamicColor()
      }
    } catch (error) {
      console.error('read settings data error:', error)
    }
  }

  private scheduleDeferredSettingsRefresh(delayMs: number = 0) {
    if (this.deferredRefreshTimer) {
      clearTimeout(this.deferredRefreshTimer)
    }

    this.deferredRefreshTimer = setTimeout(() => {
      this.deferredRefreshTimer = null
      void this.askData()
    }, delayMs)
  }
}
let setData = new Data()
export { setData }
